import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, UserPlus, Users, Search, Send, X, RotateCcw, AlertCircle, Building2,
  UserX, UserCheck, Trash2, Crown, ShieldAlert, FileDown, Eye, Activity,
  ChevronDown, ChevronUp, Mail, IdCard, CalendarClock, ScanLine, TrendingUp,
} from "lucide-react";
import { api, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { OwnerNav } from "@/components/admin/OwnerNav";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const ROLE_LABEL = { WORKSPACE_OWNER: "Owner", WORKSPACE_ADMIN: "Admin", MANAGER: "Manager", MEMBER: "Member" };
const STATUS_LABEL = { active: "Active", invited: "Invited", suspended: "Suspended", deactivated: "Deactivated", removed: "Removed" };
const REJECT_LABEL = {
  invalid_email: "Invalid email",
  already_a_member: "Already a member",
  invite_already_sent: "Invite already sent",
  seat_limit_reached: "Over seat limit",
};

const _initials = (name, email) => {
  const s = (name || email || "?").trim();
  const parts = s.split(/\s+/).filter(Boolean);
  return (parts.length >= 2 ? parts[0][0] + parts[1][0] : s.slice(0, 2)).toUpperCase();
};

const _isEmail = (s) => /^[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/.test(s || "");

// Tiering rules mirrored from backend `change_role`. Returns null if allowed, or a reason string.
function _roleChangeDisabled(viewerRole, target, viewerUserId) {
  if (target.user_id === viewerUserId) return "You can't change your own role.";
  if (target.role === "WORKSPACE_OWNER") return "The Owner's role is only changed via Transfer ownership.";
  if (viewerRole === "WORKSPACE_OWNER") return null;
  if (viewerRole === "WORKSPACE_ADMIN") {
    if (target.role === "WORKSPACE_ADMIN") return "Admins can't change another Admin's role — Owner-only.";
    return null;
  }
  return "Only Owner or Admin can change roles.";
}

function _canManageMember(viewerRole, target, viewerUserId) {
  if (viewerRole !== "WORKSPACE_OWNER" && viewerRole !== "WORKSPACE_ADMIN") return false;
  if (target.user_id === viewerUserId) return false;
  if (target.role === "WORKSPACE_OWNER") return false;
  return true;
}

// Relative time for the drawer activity feed. Falls back to locale date when > 30d.
function _relTime(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const s = Math.round((Date.now() - t) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 30 * 86400) return `${Math.floor(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

// Download a CSV endpoint by fetching it as a blob (auth Bearer via api client)
// then triggering an <a download="…"> click.
async function _downloadCsv(path, fallbackFilename) {
  try {
    const res = await api.get(path, { responseType: "blob" });
    const cd = res.headers["content-disposition"] || res.headers["Content-Disposition"] || "";
    const m = /filename="?([^";]+)"?/i.exec(cd);
    const filename = (m && m[1]) || fallbackFilename;
    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click();
    a.remove(); window.URL.revokeObjectURL(url);
  } catch (e) {
    const d = e?.response?.data;
    toast.error(typeof d === "string" ? d : (d?.detail || "Export failed"));
  }
}

export default function Team() {
  const { workspace, user } = useAuth() || {};
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [q, setQ] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null); // member row
  const [transferOpen, setTransferOpen] = useState(false);
  const [drawerUserId, setDrawerUserId] = useState(null); // A3 detail drawer

  const isCompany = workspace?.type === "company" || workspace?.type === "team";

  const load = useCallback(() => {
    if (!isCompany) { setState({ loading: false, data: null, error: null }); return; }
    setState((s) => ({ ...s, loading: true }));
    api.get("/team/members")
      .then(({ data }) => setState({ loading: false, data, error: null }))
      .catch((e) => setState({ loading: false, data: null, error: e?.response?.data?.detail || "load_failed" }));
  }, [isCompany]);
  useEffect(() => { load(); }, [load]);

  const viewerRole = state.data?.viewer?.role || "MEMBER";
  const viewerId = state.data?.viewer?.user_id || user?.id;
  const isAdminOrOwner = viewerRole === "WORKSPACE_OWNER" || viewerRole === "WORKSPACE_ADMIN";
  const isOwner = viewerRole === "WORKSPACE_OWNER";

  const filteredMembers = useMemo(() => {
    const rows = (state.data?.members || []).filter((m) => (m.status || "active") !== "removed");
    const needle = q.trim().toLowerCase();
    return needle
      ? rows.filter((m) => (m.name || "").toLowerCase().includes(needle) || (m.email || "").toLowerCase().includes(needle))
      : rows;
  }, [state.data, q]);

  const adminsForTransfer = useMemo(
    () => (state.data?.members || []).filter((m) => m.role === "WORKSPACE_ADMIN" && (m.status || "active") === "active"),
    [state.data],
  );

  const seats = state.data?.seats;
  const overSeatLimit = seats?.limit ? seats.used >= seats.limit : false;

  const handleRoleChange = async (member, nextRole) => {
    if (nextRole === member.role) return;
    try {
      await api.post(`/team/members/${member.user_id}/role`, { role: nextRole });
      toast.success(`Updated ${member.name || member.email} to ${ROLE_LABEL[nextRole]}`);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d.replaceAll("_", " ") : "Failed to change role");
    }
  };

  const handleSuspend = async (member) => {
    if (!window.confirm(`Suspend ${member.name || member.email}? They'll lose workspace access until reactivated.`)) return;
    try {
      await api.post(`/team/members/${member.user_id}/suspend`, {});
      toast.success(`${member.name || member.email} suspended`);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d.replaceAll("_", " ") : "Suspend failed");
    }
  };

  const handleReactivate = async (member) => {
    try {
      await api.post(`/team/members/${member.user_id}/reactivate`, {});
      toast.success(`${member.name || member.email} reactivated`);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d.replaceAll("_", " ") : "Reactivate failed");
    }
  };

  // Individual workspace gate — nothing else renders.
  if (!isCompany) {
    return (
      <div className="min-h-screen bg-[#050607] text-white lg:pl-64" style={{ fontFamily: "'Outfit', sans-serif" }}>
        <OwnerNav />
        <div className="mx-auto max-w-3xl px-4 py-16 sm:px-8">
          <div
            className="rounded-2xl border border-white/10 bg-white/[0.02] p-8 text-center"
            data-testid="team-individual-gate"
          >
            <Building2 className="mx-auto h-10 w-10 text-[#D6A653]" />
            <h1 className="mt-4 text-xl font-light text-white">Team management is a Company workspace feature</h1>
            <p className="mx-auto mt-2 max-w-md text-sm text-white/60">
              Your workspace is set up for a single user. Upgrade to a Company workspace to invite teammates, share leads, and split commissions.
            </p>
            <Link
              to="/billing"
              data-testid="team-individual-upgrade"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#D6A653] px-5 py-2 text-sm text-black"
            >
              Upgrade your plan
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050607] text-white lg:pl-64" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="team-page">
      <OwnerNav />
      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-8">
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-light tracking-tight text-white">Team</h1>
            <p className="mt-1 text-sm text-white/55">Manage members, invitations, and roles.</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {seats?.limit ? (
              <span
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs ${overSeatLimit ? "border-amber-500/50 text-amber-200" : "border-white/12 text-white/70"}`}
                data-testid="team-seats-chip"
              >
                <Users className="h-3.5 w-3.5" /> {seats.used} of {seats.limit} seats used
              </span>
            ) : null}
            {isAdminOrOwner && (
              <>
                <button
                  type="button"
                  onClick={() => _downloadCsv("/team/members/export.csv", "tappresence-team.csv")}
                  data-testid="team-export-list"
                  className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm font-medium text-white/85 hover:bg-white/5"
                  title="Download the team roster as CSV"
                >
                  <FileDown className="h-4 w-4" /> Export team
                </button>
                <button
                  type="button"
                  onClick={() => _downloadCsv("/team/performance/export.csv", "tappresence-performance.csv")}
                  data-testid="team-export-performance"
                  className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm font-medium text-white/85 hover:bg-white/5"
                  title="Download per-member performance as CSV"
                >
                  <FileDown className="h-4 w-4" /> Export performance
                </button>
              </>
            )}
            {isOwner && adminsForTransfer.length > 0 && (
              <button
                type="button"
                onClick={() => setTransferOpen(true)}
                data-testid="team-transfer-btn"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm font-medium text-white/85 hover:bg-white/5"
                title="Transfer workspace ownership to an Admin"
              >
                <Crown className="h-4 w-4 text-[#D6A653]" /> Transfer ownership
              </button>
            )}
            {isAdminOrOwner && (
              <button
                type="button"
                onClick={() => setInviteOpen(true)}
                data-testid="team-invite-btn"
                className="inline-flex items-center gap-2 rounded-full bg-[#D6A653] px-4 py-2 text-sm font-medium text-black hover:bg-[#c99a49]"
              >
                <UserPlus className="h-4 w-4" /> Invite teammate
              </button>
            )}
          </div>
        </header>

        {state.loading ? (
          <div className="mt-16 flex items-center justify-center gap-3 text-white/50" data-testid="team-loading">
            <Loader2 className="h-5 w-5 animate-spin text-[#D6A653]" /> Loading team…
          </div>
        ) : state.error ? (
          <div className="mt-8 rounded-2xl border border-red-500/25 bg-red-500/[0.06] p-6 text-center" data-testid="team-error">
            <AlertCircle className="mx-auto h-6 w-6 text-red-300" />
            <p className="mt-2 text-sm text-red-200">We couldn&apos;t load your team.</p>
            <button onClick={load} className="mt-3 rounded-full border border-white/15 px-3 py-1.5 text-xs text-white/80">Retry</button>
          </div>
        ) : (
          <>
            {/* Seat limit banner */}
            {overSeatLimit && (
              <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-500/30 bg-amber-500/[0.06] px-5 py-4" data-testid="team-seat-limit-banner">
                <div>
                  <p className="text-sm font-medium text-amber-200">You&apos;re at your seat limit.</p>
                  <p className="mt-0.5 text-xs text-white/60">Upgrade your plan to invite more teammates.</p>
                </div>
                <Link to="/billing" data-testid="team-seat-limit-upgrade" className="rounded-full border border-amber-500/50 bg-amber-500/10 px-3 py-1.5 text-xs text-amber-200">Upgrade plan</Link>
              </div>
            )}

            {/* Filters */}
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[220px]">
                <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-white/35" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search by name or email"
                  data-testid="team-search"
                  className="w-full rounded-lg border border-white/12 bg-black/40 py-2 pl-9 pr-3 text-sm text-white outline-none focus:border-[#D6A653]/50"
                />
              </div>
            </div>

            {/* Members table */}
            <div className="mt-6 overflow-x-auto rounded-2xl border border-white/10 bg-[#0B0D12]" data-testid="team-members-table">
              <table className="min-w-full text-sm">
                <thead className="text-left text-[11px] uppercase tracking-wider text-white/40">
                  <tr>
                    <th className="px-4 py-3">Member</th>
                    <th className="px-4 py-3">Role</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Joined</th>
                    <th className="px-4 py-3">Last activity</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="text-white/80">
                  {filteredMembers.map((m) => {
                    const roleDisabledReason = _roleChangeDisabled(viewerRole, m, viewerId);
                    const canManage = _canManageMember(viewerRole, m, viewerId);
                    const roleOptions = viewerRole === "WORKSPACE_OWNER"
                      ? ["WORKSPACE_ADMIN", "MANAGER", "MEMBER"]
                      : ["MANAGER", "MEMBER"]; // Admin: cannot promote to Admin
                    return (
                      <tr key={m.user_id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`team-member-row-${m.user_id}`}>
                        <td className="cursor-pointer px-4 py-3" onClick={() => setDrawerUserId(m.user_id)} data-testid={`team-row-view-${m.user_id}`}>
                          <div className="flex items-center gap-3">
                            <span className="grid h-8 w-8 place-items-center rounded-full bg-[#D6A653]/15 text-xs font-semibold text-[#D6A653]">{_initials(m.name, m.email)}</span>
                            <div className="min-w-0">
                              <p className="truncate text-sm font-medium text-white">{m.name || m.email}</p>
                              <p className="truncate text-xs text-white/50">{m.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs">
                          {m.role === "WORKSPACE_OWNER" ? (
                            <span className="inline-flex items-center gap-1 rounded-full border border-[#D6A653]/40 bg-[#D6A653]/[0.08] px-2 py-0.5 text-[11px] text-[#D6A653]">
                              <Crown className="h-3 w-3" /> Owner
                            </span>
                          ) : roleDisabledReason ? (
                            <span className="text-white/70" title={roleDisabledReason} data-testid={`team-row-role-locked-${m.user_id}`}>
                              {ROLE_LABEL[m.role] || m.role}
                            </span>
                          ) : (
                            <Select value={m.role} onValueChange={(v) => handleRoleChange(m, v)}>
                              <SelectTrigger className="h-8 w-[140px] border-white/15 bg-black/40 text-xs text-white" data-testid={`team-row-role-select-${m.user_id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="border-white/10 bg-[#0B0D12] text-white">
                                {roleOptions.map((r) => (
                                  <SelectItem key={r} value={r}>{ROLE_LABEL[r]}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`rounded-full border px-2 py-0.5 text-[11px] ${m.status === "active" ? "border-emerald-500/40 text-emerald-300" : m.status === "suspended" ? "border-amber-500/40 text-amber-200" : "border-white/12 text-white/55"}`}>
                            {STATUS_LABEL[m.status] || m.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-white/50">{m.joined_at ? new Date(m.joined_at).toLocaleDateString() : "—"}</td>
                        <td className="px-4 py-3 text-xs text-white/50">{m.last_activity_at ? new Date(m.last_activity_at).toLocaleDateString() : "—"}</td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2" data-testid={`team-row-actions-${m.user_id}`}>
                            {canManage ? (
                              <>
                                <button
                                  onClick={() => setDrawerUserId(m.user_id)}
                                  data-testid={`team-row-open-drawer-${m.user_id}`}
                                  className="inline-flex items-center gap-1 rounded-lg border border-white/15 px-2.5 py-1 text-xs text-white/70 hover:bg-white/5"
                                  title="View member detail"
                                >
                                  <Eye className="h-3 w-3" /> View
                                </button>
                                {m.status === "suspended" ? (
                                  <button
                                    onClick={() => handleReactivate(m)}
                                    data-testid={`team-row-reactivate-${m.user_id}`}
                                    className="inline-flex items-center gap-1 rounded-lg border border-emerald-500/40 px-2.5 py-1 text-xs text-emerald-200 hover:bg-emerald-500/10"
                                    title="Reactivate this member"
                                  >
                                    <UserCheck className="h-3 w-3" /> Reactivate
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => handleSuspend(m)}
                                    data-testid={`team-row-suspend-${m.user_id}`}
                                    className="inline-flex items-center gap-1 rounded-lg border border-white/15 px-2.5 py-1 text-xs text-white/70 hover:bg-white/5"
                                    title="Suspend workspace access"
                                  >
                                    <UserX className="h-3 w-3" /> Suspend
                                  </button>
                                )}
                                <button
                                  onClick={() => setRemoveTarget(m)}
                                  data-testid={`team-row-remove-${m.user_id}`}
                                  className="inline-flex items-center gap-1 rounded-lg border border-red-500/40 px-2.5 py-1 text-xs text-red-300 hover:bg-red-500/10"
                                  title="Remove from workspace (reassigns owned data)"
                                >
                                  <Trash2 className="h-3 w-3" /> Remove
                                </button>
                              </>
                            ) : (
                              <span className="text-xs text-white/35">—</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {filteredMembers.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-xs text-white/45" data-testid="team-empty">
                      No teammates yet. Invite someone to get started.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pending invites */}
            <PendingInvites invites={state.data?.invites || []} isAdminOrOwner={isAdminOrOwner} onChanged={load} />
          </>
        )}
      </div>

      {inviteOpen && (
        <InviteModal
          open={inviteOpen}
          onClose={() => setInviteOpen(false)}
          seats={seats}
          onDone={() => { setInviteOpen(false); load(); }}
        />
      )}

      {removeTarget && (
        <RemoveModal
          member={removeTarget}
          onClose={() => setRemoveTarget(null)}
          onDone={() => { setRemoveTarget(null); load(); }}
        />
      )}

      {transferOpen && (
        <TransferModal
          admins={adminsForTransfer}
          onClose={() => setTransferOpen(false)}
          onDone={() => { setTransferOpen(false); load(); }}
        />
      )}

      {drawerUserId && (
        <MemberDrawer
          userId={drawerUserId}
          viewerRole={viewerRole}
          viewerId={viewerId}
          onClose={() => setDrawerUserId(null)}
          onMutated={load}
          onRoleChange={handleRoleChange}
          onSuspend={handleSuspend}
          onReactivate={handleReactivate}
          onRemove={(m) => { setDrawerUserId(null); setRemoveTarget(m); }}
        />
      )}
    </div>
  );
}

function PendingInvites({ invites, isAdminOrOwner, onChanged }) {
  const revoke = async (id) => {
    if (!window.confirm("Revoke this invitation?")) return;
    try { await api.delete(`/team/invites/${id}`); toast.success("Invite revoked"); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const resend = async (id) => {
    try {
      const { data } = await api.post(`/team/invites/${id}/resend`);
      toast.success(`Invite resent (${data.resend_count}× total)`);
      onChanged();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d === "resend_rate_limit_exceeded" ? "Too many resends — wait 24h" : (d || "Failed"));
    }
  };
  if (!invites.length) return null;
  return (
    <div className="mt-10" data-testid="team-pending-section">
      <h2 className="text-sm font-medium text-white/80">Pending invitations</h2>
      <div className="mt-3 overflow-x-auto rounded-2xl border border-white/10 bg-[#0B0D12]">
        <table className="min-w-full text-sm">
          <thead className="text-left text-[11px] uppercase tracking-wider text-white/40">
            <tr>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Invited</th>
              <th className="px-4 py-3">Expires</th>
              <th className="px-4 py-3">Resends</th>
              <th className="px-4 py-3">By</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="text-white/80">
            {invites.map((i) => (
              <tr key={i.id} className="border-t border-white/5" data-testid={`team-invite-row-${i.id}`}>
                <td className="px-4 py-3">{i.email}</td>
                <td className="px-4 py-3 text-xs text-white/70">{ROLE_LABEL[i.role] || i.role}</td>
                <td className="px-4 py-3 text-xs text-white/50">{i.invited_at ? new Date(i.invited_at).toLocaleDateString() : "—"}</td>
                <td className="px-4 py-3 text-xs">{i.expired ? <span className="text-amber-300">Expired</span> : <span className="text-white/50">{new Date(i.expires_at).toLocaleDateString()}</span>}</td>
                <td className="px-4 py-3 text-xs text-white/50 tabular-nums">{i.resend_count}</td>
                <td className="px-4 py-3 text-xs text-white/50">{i.invited_by_name || "—"}</td>
                <td className="px-4 py-3">
                  {isAdminOrOwner ? (
                    <div className="flex justify-end gap-2">
                      <button onClick={() => resend(i.id)} data-testid={`team-invite-resend-${i.id}`}
                              className="inline-flex items-center gap-1 rounded-lg border border-white/15 px-2.5 py-1 text-xs text-white/70 hover:bg-white/5">
                        <RotateCcw className="h-3 w-3" /> Resend
                      </button>
                      <button onClick={() => revoke(i.id)} data-testid={`team-invite-revoke-${i.id}`}
                              className="inline-flex items-center gap-1 rounded-lg border border-red-500/40 px-2.5 py-1 text-xs text-red-300 hover:bg-red-500/10">
                        <X className="h-3 w-3" /> Revoke
                      </button>
                    </div>
                  ) : <span className="text-xs text-white/35">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function InviteModal({ open, onClose, seats, onDone }) {
  const [chips, setChips] = useState([]);
  const [buffer, setBuffer] = useState("");
  const [role, setRole] = useState("MEMBER");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [rejected, setRejected] = useState([]);
  const [seatBlocked, setSeatBlocked] = useState(false);

  const commit = () => {
    const raw = buffer.trim().replace(/,+$/, "");
    if (!raw) return;
    const items = raw.split(/[\s,;]+/).filter(Boolean);
    setChips((prev) => Array.from(new Set([...prev, ...items.map((s) => s.toLowerCase())])));
    setBuffer("");
  };
  const removeChip = (val) => setChips((prev) => prev.filter((c) => c !== val));

  const onKey = (e) => {
    if (e.key === "Enter" || e.key === "," || e.key === " ") {
      e.preventDefault(); commit();
    } else if (e.key === "Backspace" && !buffer && chips.length) {
      setChips((prev) => prev.slice(0, -1));
    }
  };

  const invalidChips = chips.filter((c) => !_isEmail(c));

  const submit = async () => {
    commit();
    const emails = chips.length ? chips : (buffer.trim() ? [buffer.trim().toLowerCase()] : []);
    if (!emails.length) { toast.error("Add at least one email"); return; }
    if (invalidChips.length) { toast.error(`Invalid: ${invalidChips.join(", ")}`); return; }
    setSubmitting(true); setRejected([]); setSeatBlocked(false);
    try {
      const { data } = await api.post("/team/invites", { emails, role, note: note || undefined });
      const okN = (data.invited || []).length;
      const rej = data.rejected || [];
      if (okN) toast.success(`Invited ${okN} teammate${okN === 1 ? "" : "s"}${rej.length ? ` — ${rej.length} skipped` : ""}`);
      setRejected(rej);
      if (okN) onDone();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.error === "seat_limit_reached") {
        setSeatBlocked(true);
        setRejected(detail.rejected || []);
      } else {
        toast.error(typeof detail === "string" ? detail : (e?.message || "Invite failed"));
      }
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="border-white/10 bg-[#0B0D12] text-white sm:max-w-lg" data-testid="team-invite-modal">
        <DialogHeader>
          <DialogTitle className="text-lg font-light text-white">Invite teammates</DialogTitle>
        </DialogHeader>

        {seats?.limit ? (
          <p className="text-xs text-white/45">{seats.used} of {seats.limit} seats used.</p>
        ) : null}

        <div className="mt-3 space-y-4">
          <label className="block">
            <span className="block text-[11px] uppercase tracking-wider text-white/50">Emails</span>
            <div className="mt-1 flex flex-wrap items-center gap-1.5 rounded-lg border border-white/15 bg-black/40 p-2">
              {chips.map((c) => (
                <span key={c} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${_isEmail(c) ? "bg-white/[0.08] text-white/85" : "bg-red-500/15 text-red-300"}`} data-testid={`chip-${c}`}>
                  {c}
                  <button type="button" onClick={() => removeChip(c)} className="text-white/50 hover:text-white">×</button>
                </span>
              ))}
              <input
                value={buffer}
                onChange={(e) => setBuffer(e.target.value)}
                onKeyDown={onKey}
                onBlur={commit}
                placeholder={chips.length ? "" : "one@company.com, two@company.com"}
                data-testid="team-invite-email-input"
                className="min-w-[160px] flex-1 bg-transparent px-1 py-1 text-sm text-white outline-none"
              />
            </div>
            <span className="mt-1 block text-[10px] text-white/40">Press Enter, space or comma to add. Backspace to remove.</span>
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-[11px] uppercase tracking-wider text-white/50">Role</span>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger className="mt-1 border-white/15 bg-black/40 text-white" data-testid="team-invite-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-white/10 bg-[#0B0D12] text-white">
                  <SelectItem value="WORKSPACE_ADMIN">Admin</SelectItem>
                  <SelectItem value="MANAGER">Manager</SelectItem>
                  <SelectItem value="MEMBER">Member</SelectItem>
                </SelectContent>
              </Select>
            </label>
          </div>

          <label className="block">
            <span className="block text-[11px] uppercase tracking-wider text-white/50">Note (optional)</span>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, 500))}
              rows={3}
              placeholder="Add a short welcome message for the invite email."
              data-testid="team-invite-note"
              className="mt-1 w-full resize-none rounded-lg border border-white/15 bg-black/40 p-2 text-sm text-white outline-none focus:border-[#D6A653]/50"
            />
            <span className="mt-1 block text-[10px] text-white/40">{note.length}/500</span>
          </label>

          {rejected.length > 0 && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/[0.06] p-3" data-testid="team-invite-rejected">
              <p className="text-xs font-medium text-amber-200">{rejected.length} email{rejected.length === 1 ? "" : "s"} skipped:</p>
              <ul className="mt-1 space-y-0.5 text-[11px] text-white/70">
                {rejected.map((r, i) => (
                  <li key={i}><span className="font-mono">{r.email}</span> — {REJECT_LABEL[r.reason] || r.reason}</li>
                ))}
              </ul>
            </div>
          )}

          {seatBlocked && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/[0.08] p-3" data-testid="team-invite-seat-blocked">
              <p className="text-sm font-medium text-amber-200">You&apos;re out of seats.</p>
              <p className="mt-1 text-xs text-white/65">Upgrade your plan to add more teammates.</p>
              <Link to="/billing" className="mt-2 inline-flex rounded-full border border-amber-500/50 bg-amber-500/10 px-3 py-1 text-xs text-amber-200">Upgrade plan</Link>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} data-testid="team-invite-cancel" className="rounded-lg border border-white/15 px-4 py-2 text-sm text-white/70">Cancel</button>
          <button onClick={submit} disabled={submitting} data-testid="team-invite-submit"
                  className="inline-flex items-center gap-2 rounded-lg bg-[#D6A653] px-4 py-2 text-sm text-black disabled:opacity-60">
            <Send className="h-4 w-4" /> {submitting ? "Sending…" : "Send invites"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RemoveModal({ member, onClose, onDone }) {
  const [state, setState] = useState({ loading: true, counts: null, reassign_to: null, error: null });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get(`/team/members/${member.user_id}/owned-counts`)
      .then(({ data }) => { if (!cancelled) setState({ loading: false, counts: data.counts, reassign_to: data.reassign_to, error: null }); })
      .catch((e) => { if (!cancelled) setState({ loading: false, counts: null, reassign_to: null, error: e?.response?.data?.detail || "load_failed" }); });
    return () => { cancelled = true; };
  }, [member.user_id]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.delete(`/team/members/${member.user_id}`);
      const r = data.reassigned || {};
      toast.success(`${member.name || member.email} removed. Reassigned ${r.leads || 0} leads, ${r.cards || 0} cards, ${r.meetings || 0} meetings.`);
      onDone();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.error === "reassignment_failed_membership_preserved") {
        toast.error("Reassignment failed — membership preserved. Try again.");
      } else {
        toast.error(typeof detail === "string" ? detail.replaceAll("_", " ") : "Remove failed");
      }
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="border-white/10 bg-[#0B0D12] text-white sm:max-w-lg" data-testid="team-remove-confirm-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg font-light text-white">
            <ShieldAlert className="h-5 w-5 text-red-300" /> Remove {member.name || member.email}?
          </DialogTitle>
        </DialogHeader>

        <div className="mt-2 space-y-4 text-sm text-white/75">
          <p>They&apos;ll lose all access to this workspace immediately. This action can be undone by re-inviting them.</p>

          {state.loading ? (
            <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.02] px-3 py-3 text-xs text-white/55">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-[#D6A653]" /> Counting owned records…
            </div>
          ) : state.error ? (
            <div className="rounded-lg border border-red-500/25 bg-red-500/[0.06] p-3 text-xs text-red-200">
              Couldn&apos;t load ownership counts. Refresh and try again.
            </div>
          ) : (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/[0.06] p-3" data-testid="team-remove-confirm-counts">
              <p className="text-xs font-medium text-amber-200">
                Owned data will be reassigned to <b>{state.reassign_to?.name || "the Owner"}</b>:
              </p>
              <ul className="mt-2 space-y-1 text-[12px] text-white/75">
                <li>Leads captured by this member: <b className="tabular-nums">{state.counts.leads}</b></li>
                <li>Cards owned by this member: <b className="tabular-nums">{state.counts.cards}</b></li>
                <li>Meetings owned by this member: <b className="tabular-nums">{state.counts.meetings}</b></li>
              </ul>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} className="rounded-lg border border-white/15 px-4 py-2 text-sm text-white/70">Cancel</button>
          <button
            onClick={submit}
            disabled={submitting || state.loading || !!state.error}
            data-testid="team-remove-confirm-submit"
            className="inline-flex items-center gap-2 rounded-lg bg-red-500/90 px-4 py-2 text-sm font-medium text-white hover:bg-red-500 disabled:opacity-60"
          >
            <Trash2 className="h-4 w-4" /> {submitting ? "Removing…" : "Remove teammate"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TransferModal({ admins, onClose, onDone }) {
  const [selected, setSelected] = useState(admins[0]?.user_id || "");
  const [phrase, setPhrase] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const target = admins.find((a) => a.user_id === selected);
  const canSubmit = !!selected && phrase === "TRANSFER" && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await api.post("/team/transfer-ownership", { new_owner_user_id: selected, confirm_phrase: phrase });
      toast.success(`Ownership transferred to ${target?.name || target?.email}`);
      onDone();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail.replaceAll("_", " ") : (detail?.error?.replaceAll("_", " ") || "Transfer failed"));
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="border-white/10 bg-[#0B0D12] text-white sm:max-w-lg" data-testid="team-transfer-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg font-light text-white">
            <Crown className="h-5 w-5 text-[#D6A653]" /> Transfer workspace ownership
          </DialogTitle>
        </DialogHeader>

        <div className="mt-2 space-y-4 text-sm text-white/75">
          <p>
            This will make <b>{target?.name || target?.email || "the selected Admin"}</b> the workspace Owner.
            You will become an Admin. This action is reversible only if the new Owner transfers ownership back to you.
          </p>

          <label className="block">
            <span className="block text-[11px] uppercase tracking-wider text-white/50">New Owner</span>
            <Select value={selected} onValueChange={setSelected}>
              <SelectTrigger className="mt-1 border-white/15 bg-black/40 text-white" data-testid="team-transfer-new-owner-select">
                <SelectValue placeholder="Select an Admin" />
              </SelectTrigger>
              <SelectContent className="border-white/10 bg-[#0B0D12] text-white">
                {admins.map((a) => (
                  <SelectItem key={a.user_id} value={a.user_id}>{a.name || a.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="mt-1 block text-[10px] text-white/40">Only active Admins are eligible.</span>
          </label>

          <label className="block">
            <span className="block text-[11px] uppercase tracking-wider text-white/50">Type <b className="text-[#D6A653]">TRANSFER</b> to confirm</span>
            <input
              value={phrase}
              onChange={(e) => setPhrase(e.target.value)}
              data-testid="team-transfer-confirm-phrase"
              placeholder="TRANSFER"
              className="mt-1 w-full rounded-lg border border-white/15 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-[#D6A653]/50"
              autoComplete="off"
            />
          </label>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} className="rounded-lg border border-white/15 px-4 py-2 text-sm text-white/70">Cancel</button>
          <button
            onClick={submit}
            disabled={!canSubmit}
            data-testid="team-transfer-submit"
            className="inline-flex items-center gap-2 rounded-lg bg-[#D6A653] px-4 py-2 text-sm font-medium text-black hover:bg-[#c99a49] disabled:opacity-60"
          >
            <Crown className="h-4 w-4" /> {submitting ? "Transferring…" : "Transfer ownership"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ═════════════════════════════════════════════════════════════════════════
// A3 — Member detail drawer
// ═════════════════════════════════════════════════════════════════════════
function MemberDrawer({ userId, viewerRole, viewerId, onClose, onMutated, onRoleChange, onSuspend, onReactivate, onRemove }) {
  const [detail, setDetail] = useState({ loading: true, data: null, error: null });
  const [expanded, setExpanded] = useState({ leads: true, meetings: false, cards: false });

  useEffect(() => {
    let cancelled = false;
    api.get(`/team/members/${userId}`)
      .then(({ data }) => { if (!cancelled) setDetail({ loading: false, data, error: null }); })
      .catch((e) => { if (!cancelled) setDetail({ loading: false, data: null, error: e?.response?.data?.detail || "load_failed" }); });
    return () => { cancelled = true; };
  }, [userId]);

  const identity = detail.data?.identity;
  const isSelf = userId === viewerId;
  const canManage = !isSelf
    && identity?.role !== "WORKSPACE_OWNER"
    && (viewerRole === "WORKSPACE_OWNER" || viewerRole === "WORKSPACE_ADMIN");
  const roleOptions = viewerRole === "WORKSPACE_OWNER"
    ? ["WORKSPACE_ADMIN", "MANAGER", "MEMBER"]
    : ["MANAGER", "MEMBER"];

  return (
    <Sheet open={true} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        side="right"
        className="w-full overflow-y-auto border-l border-white/10 bg-[#0B0D12] p-0 text-white sm:max-w-2xl"
        data-testid="team-member-drawer"
      >
        <SheetHeader className="sticky top-0 z-10 flex-row items-center justify-between border-b border-white/8 bg-[#0B0D12]/95 px-6 py-4 backdrop-blur">
          <SheetTitle className="text-base font-light text-white">Team member</SheetTitle>
          <button onClick={onClose} data-testid="team-drawer-close" className="rounded-full p-1 text-white/60 hover:bg-white/5">
            <X className="h-4 w-4" />
          </button>
        </SheetHeader>

        {detail.loading ? (
          <div className="flex h-64 items-center justify-center gap-2 text-white/50">
            <Loader2 className="h-4 w-4 animate-spin text-[#D6A653]" /> Loading member…
          </div>
        ) : detail.error ? (
          <div className="m-6 rounded-2xl border border-red-500/25 bg-red-500/[0.06] p-6 text-center text-sm text-red-200">
            Couldn&apos;t load member — {detail.error}
          </div>
        ) : (
          <div className="space-y-6 p-6">
            {/* Identity */}
            <section data-testid="team-drawer-identity" className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-4">
                <span className="grid h-14 w-14 place-items-center rounded-full bg-[#D6A653]/15 text-lg font-semibold text-[#D6A653]">
                  {_initials(identity.name, identity.email)}
                </span>
                <div className="min-w-0">
                  <h2 className="truncate text-xl font-light text-white">{identity.name || identity.email}</h2>
                  <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-white/55"><Mail className="h-3 w-3" /> {identity.email}</p>
                  <div className="mt-2 flex items-center gap-2">
                    <span data-testid="team-drawer-role-pill"
                          className={`rounded-full border px-2 py-0.5 text-[11px] ${identity.role === "WORKSPACE_OWNER" ? "border-[#D6A653]/40 bg-[#D6A653]/[0.08] text-[#D6A653]" : "border-white/15 text-white/70"}`}>
                      {ROLE_LABEL[identity.role] || identity.role}
                    </span>
                    <span data-testid="team-drawer-status-pill"
                          className={`rounded-full border px-2 py-0.5 text-[11px] ${identity.status === "active" ? "border-emerald-500/40 text-emerald-300" : identity.status === "suspended" ? "border-amber-500/40 text-amber-200" : "border-white/15 text-white/55"}`}>
                      {STATUS_LABEL[identity.status] || identity.status}
                    </span>
                    <span className="text-[11px] text-white/45">Joined {identity.joined_at ? new Date(identity.joined_at).toLocaleDateString() : "—"}</span>
                  </div>
                </div>
              </div>
            </section>

            {/* Admin actions bar */}
            {canManage && (
              <section className="flex flex-wrap items-center gap-2 rounded-xl border border-white/10 bg-white/[0.02] p-3" data-testid="team-drawer-admin-actions">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] uppercase tracking-wider text-white/50">Change role</span>
                  <Select value={identity.role} onValueChange={(v) => { onRoleChange({ user_id: userId, role: identity.role, name: identity.name, email: identity.email }, v); onClose(); }}>
                    <SelectTrigger className="h-8 w-[140px] border-white/15 bg-black/40 text-xs text-white" data-testid="team-drawer-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="border-white/10 bg-[#0B0D12] text-white">
                      {roleOptions.map((r) => <SelectItem key={r} value={r}>{ROLE_LABEL[r]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="ml-auto flex gap-2">
                  {identity.status === "suspended" ? (
                    <button
                      onClick={() => { onReactivate({ user_id: userId, name: identity.name, email: identity.email }); onClose(); }}
                      data-testid="team-drawer-reactivate"
                      className="inline-flex items-center gap-1 rounded-lg border border-emerald-500/40 px-2.5 py-1 text-xs text-emerald-200 hover:bg-emerald-500/10"
                    ><UserCheck className="h-3 w-3" /> Reactivate</button>
                  ) : (
                    <button
                      onClick={() => { onSuspend({ user_id: userId, name: identity.name, email: identity.email }); onClose(); }}
                      data-testid="team-drawer-suspend"
                      className="inline-flex items-center gap-1 rounded-lg border border-white/15 px-2.5 py-1 text-xs text-white/70 hover:bg-white/5"
                    ><UserX className="h-3 w-3" /> Suspend</button>
                  )}
                  <button
                    onClick={() => onRemove({ user_id: userId, name: identity.name, email: identity.email })}
                    data-testid="team-drawer-remove"
                    className="inline-flex items-center gap-1 rounded-lg border border-red-500/40 px-2.5 py-1 text-xs text-red-300 hover:bg-red-500/10"
                  ><Trash2 className="h-3 w-3" /> Remove</button>
                </div>
              </section>
            )}

            {/* KPI grid */}
            <section className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <KpiTile testid="team-drawer-kpi-leads" icon={<TrendingUp className="h-3.5 w-3.5" />}
                       label="Leads captured"
                       value={detail.data.kpis.leads_captured_all}
                       hint={`${detail.data.kpis.leads_captured_this_week} this week · ${detail.data.kpis.leads_captured_this_month} this month`} />
              <KpiTile testid="team-drawer-kpi-cards" icon={<IdCard className="h-3.5 w-3.5" />}
                       label="Cards published" value={detail.data.kpis.cards_published} />
              <KpiTile testid="team-drawer-kpi-meetings" icon={<CalendarClock className="h-3.5 w-3.5" />}
                       label="Meetings" value={detail.data.kpis.meetings_booked_upcoming + detail.data.kpis.meetings_booked_past}
                       hint={`${detail.data.kpis.meetings_booked_upcoming} upcoming · ${detail.data.kpis.meetings_booked_past} past`} />
              <KpiTile testid="team-drawer-kpi-scanner" icon={<ScanLine className="h-3.5 w-3.5" />}
                       label="Scanner uses" value={detail.data.kpis.scanner_uses} />
              <KpiTile testid="team-drawer-kpi-conversion" icon={<Activity className="h-3.5 w-3.5" />}
                       label="Conversion" value={`${detail.data.kpis.conversion_rate_pct}%`}
                       hint="Meetings / leads" />
            </section>

            {/* Activity feed */}
            <section data-testid="team-drawer-activity">
              <h3 className="text-sm font-medium text-white/85">Recent activity</h3>
              <div className="mt-3 rounded-xl border border-white/10 bg-white/[0.02] p-3">
                {detail.data.activity.length === 0 ? (
                  <p className="text-xs text-white/45">No activity yet.</p>
                ) : (
                  <ul className="space-y-2">
                    {detail.data.activity.map((ev, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-[#D6A653]/60" />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-xs text-white/80"><b>{_activityLabel(ev.type)}</b> · {ev.subject}</p>
                          <p className="text-[10px] text-white/45">{_relTime(ev.timestamp)}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>

            {/* Recent leads / meetings / cards */}
            <MiniSection testid="team-drawer-recent-leads" exportTestid="team-drawer-export-leads"
                         label="Recent leads" userId={userId} kind="leads"
                         expanded={expanded.leads} onToggle={() => setExpanded((s) => ({ ...s, leads: !s.leads }))} />
            <MiniSection testid="team-drawer-recent-meetings" exportTestid="team-drawer-export-meetings"
                         label="Recent meetings" userId={userId} kind="meetings"
                         expanded={expanded.meetings} onToggle={() => setExpanded((s) => ({ ...s, meetings: !s.meetings }))} />
            <MiniSection testid="team-drawer-recent-cards" exportTestid="team-drawer-export-cards"
                         label="Recent cards" userId={userId} kind="cards"
                         expanded={expanded.cards} onToggle={() => setExpanded((s) => ({ ...s, cards: !s.cards }))} />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function KpiTile({ testid, icon, label, value, hint }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-white/50">{icon} {label}</div>
      <p className="mt-1 text-2xl font-light tabular-nums text-white">{value}</p>
      {hint ? <p className="mt-0.5 text-[10px] text-white/45">{hint}</p> : null}
    </div>
  );
}

function _activityLabel(t) {
  switch (t) {
    case "lead_captured": return "Lead captured";
    case "card_created": return "Card created";
    case "card_published": return "Card published";
    case "meeting_booked": return "Meeting booked";
    default: return t;
  }
}

function MiniSection({ testid, exportTestid, label, userId, kind, expanded, onToggle }) {
  const [state, setState] = useState({ loading: false, items: null, total: 0, error: null });
  useEffect(() => {
    if (!expanded || state.items) return;
    setState((s) => ({ ...s, loading: true }));
    api.get(`/team/members/${userId}/${kind}?limit=10`)
      .then(({ data }) => setState({ loading: false, items: data.items || [], total: data.total || 0, error: null }))
      .catch((e) => setState({ loading: false, items: null, total: 0, error: e?.response?.data?.detail || "load_failed" }));
  }, [expanded, state.items, userId, kind]);

  return (
    <section className="rounded-xl border border-white/10 bg-white/[0.02]" data-testid={testid}>
      <button
        type="button" onClick={onToggle}
        className="flex w-full items-center justify-between px-3 py-2.5 text-left text-sm text-white/85 hover:bg-white/5"
      >
        <span>{label}{state.total ? ` (${state.total})` : ""}</span>
        <span className="flex items-center gap-2">
          <span
            onClick={(e) => { e.stopPropagation(); _downloadCsv(`/team/members/${userId}/${kind}/export.csv`, `tappresence-${kind}-${userId}.csv`); }}
            data-testid={exportTestid}
            className="inline-flex cursor-pointer items-center gap-1 rounded-lg border border-white/15 px-2 py-0.5 text-[11px] text-white/70 hover:bg-white/5"
            role="button"
            tabIndex={0}
          >
            <FileDown className="h-3 w-3" /> CSV
          </span>
          {expanded ? <ChevronUp className="h-4 w-4 text-white/40" /> : <ChevronDown className="h-4 w-4 text-white/40" />}
        </span>
      </button>
      {expanded && (
        <div className="border-t border-white/8 p-3">
          {state.loading ? (
            <p className="text-xs text-white/45">Loading…</p>
          ) : state.error ? (
            <p className="text-xs text-red-300">Couldn&apos;t load {label.toLowerCase()}.</p>
          ) : !state.items || state.items.length === 0 ? (
            <p className="text-xs text-white/45">Nothing to show yet.</p>
          ) : (
            <ul className="space-y-1.5">
              {state.items.map((it, i) => (
                <li key={i} className="flex items-center justify-between gap-2 rounded-md px-1 py-1 text-xs text-white/75">
                  {kind === "leads" && <><span className="truncate">{it.name || it.email || "—"}</span><span className="text-[10px] text-white/45">{new Date(it.created_at).toLocaleDateString()}</span></>}
                  {kind === "meetings" && <><span className="truncate">{it.meeting_type_title || "Meeting"}{it.attendee?.name ? ` · ${it.attendee.name}` : ""}</span><span className="text-[10px] text-white/45">{it.start_utc ? new Date(it.start_utc).toLocaleDateString() : "—"}</span></>}
                  {kind === "cards" && <><span className="truncate">{it.identity?.fullName || it.slug || "Card"}</span><span className="text-[10px] text-white/45">{it.status}</span></>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
